// https://kit.svelte.dev/docs/hooks#shared-hooks
import * as Sentry from '@sentry/sveltekit';
import type { HandleClientError } from '@sveltejs/kit';
import { _ } from 'svelte-i18n';
import { get } from 'svelte/store';
import { dev } from '$app/environment';
import { PUBLIC_BUILD_NAME } from '$env/static/public';

type ErrorWithResponse = Partial<Error> & {
    status?: number;
    data?: Record<string, unknown>;
    response?: {
        status?: number;
    };
};

// polyfill for crypto.randomUUID
(() => {
    if (typeof crypto === 'undefined' || !crypto.randomUUID) {
        // window.crypto 객체가 없다면 정의
        if (typeof window !== 'undefined' && !window.crypto) {
            Object.defineProperty(window, 'crypto', {
                value: {} as Crypto,
                configurable: true,
            });
        }

        const cryptoObj = typeof window !== 'undefined' ? window.crypto : crypto;

        // 새로운 UUID 생성 로직 적용
        cryptoObj.randomUUID = function () {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = (Math.random() * 16) | 0,
                    v = c == 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            }) as `${string}-${string}-${string}-${string}-${string}`;
        };
    }
})();

if (!dev && process.env.USE_SENTRY) {
    Sentry.init({
        dsn: 'https://cdba35afac9373f06545b96354a60594@o4505821044867073.ingest.sentry.io/4505821047947264',
        environment: PUBLIC_BUILD_NAME,
        // integrations: [new Sentry.Replay()],
        // Performance Monitoring
        tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
        // Session Replay
        // replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        // replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        beforeSend(event, hint) {
            // 401 Unauthorized는 인증 만료로 인한 정상 흐름이므로 Sentry에 보내지 않음
            const error = hint?.originalException as any;
            if (error?.response?.status === 401 || error?.status === 401) {
                return null;
            }

            event.tags = {
                ...event.tags,
                build_date: __BUILD_DATE__,
                errorId: crypto.randomUUID(),
            };
            return event;
        },
    });
}

export const handleError: HandleClientError = async ({ error, event }) => {
    const errorId = crypto.randomUUID();
    const normalizedError = error as ErrorWithResponse;
    if (!dev) {
        Sentry.captureException(error, { extra: { event, errorId } });
    } else {
        console.warn('%c⚠️ handleError Event:', 'color: yellow; background: black; font-size: 16px', event);
        console.error('🔥', error);
    }

    const status = normalizedError.response?.status || normalizedError.status;
    return {
        errorId,
        status,
        message: status === 403 ? get(_)('error.403') : get(_)('error.404'),
        // fail action이 throw 되면 extra data를 전달 받음 message, status 등 body에 extra 데이터가 담겨질 수 있음.
        ...(normalizedError.data || {}),
    };
};
